import { findLastIndex, isNil, range } from 'lodash';
import moment from 'moment';
import {
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  View as RNView,
  ScrollView,
  ScrollViewProps,
  StyleProp,
  StyleSheet,
  ViewStyle,
} from 'react-native';
import { Text } from '@shared/components/Text';
import { MBHover } from '@shared/components/hover';
import { MBIconV2 } from '@shared/components/image';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { ISOString } from '@shared/scenes/cards/agenda/utils';
import { ColorThemeContext, Colors, SharedStyles, grid } from '@shared/styles';
import { formatTime } from '@shared/util/date';

const MINUTE_INCREMENT = 15;
const delta = 1000 * 60 * MINUTE_INCREMENT;
const numOptions = (1000 * 60 * 60 * 24) / delta;
const options = range(numOptions).map(i => i * delta);
const itemHeight = grid(4);

interface Props {
  value: string;
  style?: StyleProp<ViewStyle>;
  onChange(dateString: ISOString): void;
  /*
        Optional close handler that will render a top bar with a close "X" icon when provided.
    */
  onClose?(): void;
  showClose?: boolean;
}

interface UseScrollCurrentIndexIntoViewResponse {
  scrollView: MutableRefObject<ScrollView | null>;
  onScroll: ScrollViewProps['onScroll'];
  onContentSizeChange: ScrollViewProps['onContentSizeChange'];
  onLayout: ScrollViewProps['onLayout'];
}

function useScrollCurrentIndexIntoView(
  currentIndex: number,
): UseScrollCurrentIndexIntoViewResponse {
  const [height, setHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [contentOffset, setContentOffset] = useState(0);

  const onScroll: ScrollViewProps['onScroll'] = useCallback(event => {
    setContentOffset(event.nativeEvent.contentOffset.y);
  }, []);

  const onContentSizeChange: ScrollViewProps['onContentSizeChange'] = useCallback((_w, h) => {
    setContentHeight(h);
  }, []);

  const onLayout: ScrollViewProps['onLayout'] = useCallback(event => {
    setHeight(event.nativeEvent.layout.height);
  }, []);

  const scrollView = useRef<ScrollView>(null);
  const loaded = !!height && !!contentHeight;

  useEffect(() => {
    const topPadding = itemHeight * 2;
    const itemYStart = currentIndex * itemHeight;

    if (!loaded) {
      scrollView.current?.scrollTo({
        y: itemYStart - topPadding,
        animated: true,
      });
    }
  }, [currentIndex, height, contentHeight, contentOffset, loaded]);

  return useMemo(
    () => ({ scrollView, onScroll, onContentSizeChange, onLayout }),
    [scrollView, onScroll, onContentSizeChange, onLayout],
  );
}

export function MBTimePicker({ value, style, onChange, onClose, showClose }: Props) {
  const colors = useContext(ColorThemeContext);
  const currentScrollIndex = useMemo(() => {
    return findLastIndex(options, offset =>
      moment(value).startOf('day').add(offset, 'milliseconds').isSameOrBefore(value),
    );
  }, [value]);

  const { scrollView, onScroll, onContentSizeChange, onLayout } =
    useScrollCurrentIndexIntoView(currentScrollIndex);

  const dates = useMemo(() => {
    const dateArray = options.map(offset =>
      moment(value || undefined)
        .startOf('day')
        .add(offset, 'milliseconds'),
    );
    if (moment(value).isValid() && !dateArray.some(date => date.isSame(moment(value), 'minute'))) {
      dateArray.push(moment(value));
    }
    return dateArray.sort((a, b) => a.diff(b));
  }, [value]);

  return (
    <RNView style={styles.container}>
      {showClose && !isNil(onClose) && (
        <RNView style={styles.bar}>
          <MBTouchableOpacity onPress={onClose}>
            <MBIconV2 name="exit-small" color={colors.foregroundInactive} size={grid(2)} />
          </MBTouchableOpacity>
        </RNView>
      )}
      <ScrollView
        style={style}
        ref={scrollView}
        onScroll={onScroll}
        onContentSizeChange={onContentSizeChange}
        onLayout={onLayout}
        scrollEventThrottle={1000}
        keyboardShouldPersistTaps="always"
        nestedScrollEnabled={false}
      >
        {dates.map((date, index) => (
          <MBTouchableOpacity key={index} onPress={() => onChange(date.toISOString())}>
            <MBHover
              center
              hoverStyle={{ backgroundColor: colors.input.backgroundInactive }}
              style={[
                SharedStyles.borderRadius,
                { height: itemHeight },
                date.isSame(value, 'minute') && { backgroundColor: colors.input.backgroundActive },
              ]}
            >
              <Text size="body1" align="center">
                {formatTime(date.toISOString(), false)}
              </Text>
            </MBHover>
          </MBTouchableOpacity>
        ))}
      </ScrollView>
    </RNView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginTop: grid(1),
    borderColor: Colors.v2.gray05,
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: grid(1),
    paddingHorizontal: grid(0.5),
  },
  bar: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingTop: grid(1),
    paddingHorizontal: grid(0.75),
  },
});
